import React, { FC } from "react";
import { TIMER_TIMELINE, TIMER_TYPE } from "../types/CommonTypes";
import { DATE_TYPE } from "../../../utils/Types";
import { formatMilliSeconds } from "../../../helpers/formatMilliSeconds";
import formatDateTime from "../../../helpers/formatDateTime";
import { Box, Typography } from "@mui/material";
import { globalStyles } from "../../../styles/GlobalStyles";
import Icons from "../../../assets/Icons";
import { useTranslation } from "react-i18next";
import { toggleEditTimerLabelDrawer } from "./EditTimersLabelDrawer";
import NoteChip from "./NoteChip";

interface TimerNoteTimelineProps {
  item: TIMER_TIMELINE;
  hideLine?: boolean;
  isNotLast?: boolean;
  isEditMode?: boolean;
  timelineItemIndex: number;
}

function getTimerDisplayValue(
  timestamp: DATE_TYPE,
  duration?: number,
  action?: string,
) {
  if (action === "CREATE" && duration) {
    return formatMilliSeconds(duration);
  }

  if (timestamp) {
    return formatDateTime(timestamp, true, "hh:mm:ss aaa");
  }

  return "";
}

const TimerNoteTimeline: FC<TimerNoteTimelineProps> = ({
  item,
  hideLine = false,
  isNotLast = false,
  isEditMode = false,
  timelineItemIndex,
}) => {
  const {
    timestamp,
    desc,
    loggedBy,
    title,
    duration,
    action,
    caseId,
    timerId,
    timerCreatedAt,
    timerType,
  } = item;

  const { t } = useTranslation();

  const getTimerLabel = () => {
    switch (timerType) {
      case TIMER_TYPE.ALARM:
        return t("Alarm");

      case TIMER_TYPE.STOPWATCH:
        return t("Stopwatch");

      default:
        return t("Timer");
    }
  };

  const timerLabel = getTimerLabel();

  const timerDisplayValue = getTimerDisplayValue(
    timestamp,
    duration,
    action,
  );

  const handleTimerLabelClick = () => {
    if (isEditMode) {
      toggleEditTimerLabelDrawer({
        caseId: caseId ? parseInt(caseId) : undefined,
        timerId,
        description: desc,
        timerDuration: duration,
        timerCreatedAt,
        timelineItemIndex,
      });
    }
  };

  return (
    <Box sx={globalStyles.row}>
      {!hideLine && (
        <Box sx={styles.timelineColumn}>
          <Icons.VoiceNote width={24} height={24} />

          {isNotLast && <Box sx={styles.line} />}
        </Box>
      )}

      <Box
        sx={[
          styles.timerCard,
          !hideLine && isNotLast && styles.cardSpacing,
          hideLine && styles.compactCard,
        ]}
      >
        {/* Title + time/duration */}
        <Box sx={styles.headerRow}>
          <Typography sx={styles.title}>
            {title ?? timerLabel}
          </Typography>

          {timerDisplayValue && (
            <Typography sx={styles.headerValue}>
              {timerDisplayValue}
            </Typography>
          )}
        </Box>

        {/* Timer description */}
        {desc && (
          <Typography
            onClick={handleTimerLabelClick}
            sx={[
              styles.description,
              isEditMode && styles.editModeTextBox,
            ]}
          >
            {desc}
          </Typography>
        )}

        {/* Logged metadata */}
        <Typography sx={styles.metaText}>
          {t("Logged_at_and_by", {
            logged_at: formatDateTime(
              timestamp,
              true,
              "hh:mm:ss aaa",
            ),
            logged_by: loggedBy,
          })}
        </Typography>

        {/* Timer type */}
        <Box sx={styles.chipContainer}>
          <NoteChip
            icon={<Icons.TimeHistory />}
            label={timerLabel}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  timelineColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: "12px",
  },

  line: {
    width: "4px",
    flex: 1,
    borderRadius: "4px",
    backgroundColor: "custom.background.activity",
  },

  timerCard: {
    flex: 1,
    minWidth: 0,
    minHeight: "88px",

    backgroundColor: "custom.background.primary",

    border: "1px solid #E5E7EB",
    borderRadius: "8px",

    px: "12px",
    py: "10px",

    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cardSpacing: {
    mb: "16px",
  },

  compactCard: {
    width: "100%",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",

    columnGap: "6px",
    rowGap: "4px",

    width: "100%",
  },

  title: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,

    color: "custom.foreground.primary",
  },

  headerValue: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,

    color: "custom.foreground.primary",
  },

  description: {
    mt: "3px",

    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: 400,

    color: "custom.foreground.primary",

    border: "1px solid transparent",
    borderRadius: "4px",

    width: "fit-content",
    maxWidth: "100%",

    px: "3px",
    ml: "-3px",

    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  editModeTextBox: {
    borderColor: "#79747E",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },

  metaText: {
    mt: "3px",

    fontSize: "12px",
    lineHeight: "16px",

    color: "custom.foreground.secondary",
  },

  chipContainer: {
    mt: "6px",

    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
};

export default TimerNoteTimeline;